import {
  ExtrudedPolygonAlgorithmType,
  GeometricAlgorithmType,
  MeshAlgorithmType,
  MultiPolygon2AlgorithmType,
  MultiPolygonAlgorithmType,
  Polyline2AlgorithmType,
} from './geometry/algorithm-types';
import { GeometricProperty } from './geometry/geometric-types';
import { sparkelPropertySet } from './property-operations';

// Friendly properties are selected fields of the BIM model that are extracted and grouped with Sparkel properties
export enum FriendlyAttribute {
  Layer = 'Layer',
  Level = 'Level',
  BaseConstraint = 'Base Constraint',
  BaseLevel = 'Base Level',
  IfcName = 'IfcName',
  Type = 'Type',
  IfcClass = 'IfcClass',
  SourceFile = 'Source File',
  ShapeName = 'Name',
  ShapeType = 'Type',
  ShapeFolder = 'Folder',
  PageNumber = 'Page Number',
  Category = 'Sparkel Category',
  TypeName = 'Type Name',
  ProjectName = 'Project Name',
  IfcProjectName = 'Project Name(Other)',
  RoofUValue = 'RoofUValue',
  IfcDistributionSystem = 'IfcDistributionSystem',
  Description = 'Description',
}

// An ordered list of chosen property categories and properties
export const FRIENDLY_PROPERTIES: [string, FriendlyAttribute][] = [
  ['Element', FriendlyAttribute.IfcName],
  ['Element', FriendlyAttribute.Type],
  ['Element', FriendlyAttribute.Category],
  ['Item', FriendlyAttribute.Layer],
  ['Item', FriendlyAttribute.SourceFile],
  ['Identity Data', FriendlyAttribute.TypeName],
  ['Constraints', FriendlyAttribute.Level],
  ['Constraints', FriendlyAttribute.BaseConstraint],
  ['Constraints', FriendlyAttribute.BaseLevel],
  ['Other', FriendlyAttribute.ProjectName],
  ['Project', FriendlyAttribute.IfcProjectName],
  ['Shape', FriendlyAttribute.ShapeName],
  ['Shape', FriendlyAttribute.ShapeType],
  ['Shape', FriendlyAttribute.ShapeFolder],
  ['Shape', FriendlyAttribute.SourceFile],
  ['Shape', FriendlyAttribute.PageNumber],
  [sparkelPropertySet, FriendlyAttribute.RoofUValue],
  [sparkelPropertySet, FriendlyAttribute.IfcDistributionSystem],
  ['IFC Attributes', FriendlyAttribute.IfcClass],
  ['IFC Attributes', FriendlyAttribute.Description],
];

// Override the displayed name of a geometric property
// eslint-disable-next-line complexity
export const displayNameFromFriendlyAttribute = (
  type: FriendlyAttribute,
  translate: (key: string) => string = (key) => key
): string => {
  switch (type as FriendlyAttribute) {
    case FriendlyAttribute.IfcName:
      return translate('common:element-name');
    case FriendlyAttribute.SourceFile:
      return translate('common:source-file');
    case FriendlyAttribute.PageNumber:
      return translate('common:page-number');
    case FriendlyAttribute.IfcClass:
      return translate('common:element-category');
    case FriendlyAttribute.Layer:
      return translate('common:element-level');
    case FriendlyAttribute.BaseConstraint:
      return translate('common:element-level');
    case FriendlyAttribute.BaseLevel:
      return translate('common:element-level');
    case FriendlyAttribute.TypeName:
      return translate('common:element-name');
    case FriendlyAttribute.IfcProjectName:
      return translate('common:project-name');
    case FriendlyAttribute.ShapeName:
      return translate('common:shape-name');
    case FriendlyAttribute.ShapeFolder:
      return translate('common:shape-folder');
    case FriendlyAttribute.ShapeType:
      return translate('common:shape-type');
    case FriendlyAttribute.Category:
      return translate('common:element-category');
    case FriendlyAttribute.Type:
      return translate('common:element-type');
    case FriendlyAttribute.Level:
      return translate('common:element-level');
    case FriendlyAttribute.ProjectName:
      return translate('common:project-name');
    case FriendlyAttribute.RoofUValue:
      return 'Roof U-Value';
    default:
      return type;
  }
};

// Override the displayed name of a geometric property
const displayNameMap = (
  translate: (key: string) => string = (key) => key
): Record<GeometricProperty, string> => {
  return {
    [GeometricProperty.LENGTH]: translate('common:quantity.length'),
    [GeometricProperty.HEIGHT]: translate('common:quantity.height'),
    [GeometricProperty.WIDTH]: translate('common:quantity.width'),
    [GeometricProperty.CIRCUMFERENCE]: translate('common:quantity.perimeter'),
    [GeometricProperty.LARGEST_SIDE_AREA]: translate(
      'common:quantity.net-area'
    ),
    [GeometricProperty.GROSS_SIDE_AREA]: translate(
      'common:quantity.gross-area'
    ),
    [GeometricProperty.TOP_SURFACE_AREA]: translate(
      'common:quantity.top-surface-area'
    ),
    [GeometricProperty.BOTTOM_SURFACE_AREA]: translate(
      'common:quantity.bottom-surface-area'
    ),
    [GeometricProperty.VOLUME]: translate('common:quantity.volume'),
    [GeometricProperty.TOTAL_SURFACE_AREA]: translate(
      'common:quantity.total-surface-area'
    ),
    [GeometricProperty.THICKNESS]: translate('common:quantity.thickness'),
    [GeometricProperty.EXTRUDED_SIDE_AREA]: translate(
      'common:quantity.extruded-side-area'
    ),
    [GeometricProperty.DIAMETER]: translate('common:quantity.diameter'),
  };
};

//Note, this function is used a lot in name mapping. Take care that dynamic linking and grouping takes care when eventual changes are made
export const displayNameFromProperty = (
  type: GeometricProperty | string,
  translate: (key: string) => string = (key) => key
): string => {
  return displayNameMap(translate)[type as GeometricProperty] ?? type;
};

// Override the displayed name of an algorithm
// eslint-disable-next-line complexity
export const displayNameFromAlgorithm = (
  algorithmType: GeometricAlgorithmType,
  translate: (key: string) => string = (key) => key
): string => {
  switch (algorithmType) {
    case MeshAlgorithmType.TOTAL_MESH_AREA:
      return translate('common:calculation.total-mesh-area');
    case MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA:
      return translate('common:calculation.convex-largest-flat-side-area');
    case MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY:
      return translate(
        'common:calculation.convex-largest-flat-side-area-infinity'
      );
    case MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA:
      return translate('common:calculation.largest-flat-surface-area');
    case MeshAlgorithmType.LARGEST_SURFACE_AREA:
      return translate('common:calculation.largest-surface-area');
    case MeshAlgorithmType.LARGEST_SURFACE_AREA_WITHOUT_OPENINGS:
      return translate(
        'common:calculation.largest-surface-area-without-openings'
      );
    case MeshAlgorithmType.MESH_PERIMETER:
      return translate('common:calculation.mesh-perimeter');
    case MeshAlgorithmType.BOTTOM_SURFACE_AREA:
      return translate('common:calculation.bottom-surface-area');
    case MeshAlgorithmType.TOP_SURFACE_AREA:
      return translate('common:calculation.top-surface-area');
    case MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH:
      return translate('common:calculation.bounding-box-horizontal-length');
    case MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH:
      return translate('common:calculation.bounding-box-horizontal-width');
    case MeshAlgorithmType.BOUNDING_BOX_LENGTH:
      return translate('common:calculation.bounding-box-length');
    case MeshAlgorithmType.TETRAHEDRON_VOLUME:
      return translate('common:calculation.tetrahedron-volume');
    case MeshAlgorithmType.VERTICAL_COMPONENT:
      return translate('common:calculation.vertical-component');
    case MeshAlgorithmType.MESH_LOCATION_MIN_X:
      return translate('common:calculation.location-min-x');
    case MeshAlgorithmType.MESH_LOCATION_MIN_Y:
      return translate('common:calculation.location-min-y');
    case MeshAlgorithmType.MESH_LOCATION_MIN_Z:
      return translate('common:calculation.location-min-z');
    case MeshAlgorithmType.MESH_LOCATION_MAX_X:
      return translate('common:calculation.location-max-x');
    case MeshAlgorithmType.MESH_LOCATION_MAX_Y:
      return translate('common:calculation.location-max-y');
    case MeshAlgorithmType.MESH_LOCATION_MAX_Z:
      return translate('common:calculation.location-max-z');

    case MultiPolygonAlgorithmType.POLYGON_GROSS_SIDE_AREA:
      return translate('common:calculation.shape.gross-side-area');
    case MultiPolygonAlgorithmType.POLYGON_AREA:
      return translate('common:calculation.shape.largest-side-area');
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_X:
      return translate('common:calculation.location-min-x');
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_Y:
      return translate('common:calculation.location-min-y');
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_Z:
      return translate('common:calculation.location-min-z');
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_X:
      return translate('common:calculation.location-max-x');
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_Y:
      return translate('common:calculation.location-max-y');
    case MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_Z:
      return translate('common:calculation.location-max-z');

    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_X:
      return translate('common:calculation.location-min-x');
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_Y:
      return translate('common:calculation.location-min-y');
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_Z:
      return translate('common:calculation.location-min-z');
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_X:
      return translate('common:calculation.location-max-x');
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_Y:
      return translate('common:calculation.location-max-y');
    case ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_Z:
      return translate('common:calculation.location-max-z');

    case MultiPolygon2AlgorithmType.POLYGON2_GROSS_SIDE_AREA:
      return translate('common:calculation.sheet-shape.gross-side-area');
    case MultiPolygon2AlgorithmType.POLYGON2_AREA:
      return translate('common:calculation.sheet-shape.largest-side-area');
    case MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MIN_X:
      return translate('common:calculation.location-min-x');
    case MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MIN_Y:
      return translate('common:calculation.location-min-y');
    case MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MAX_X:
      return translate('common:calculation.location-max-x');
    case MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MAX_Y:
      return translate('common:calculation.location-max-y');

    case Polyline2AlgorithmType.LINE2_LOCATION_MIN_X:
      return translate('common:calculation.location-min-x');
    case Polyline2AlgorithmType.LINE2_LOCATION_MIN_Y:
      return translate('common:calculation.location-min-y');
    case Polyline2AlgorithmType.LINE2_LOCATION_MAX_X:
      return translate('common:calculation.location-max-x');
    case Polyline2AlgorithmType.LINE2_LOCATION_MAX_Y:
      return translate('common:calculation.location-max-y');

    default:
      return algorithmType;
  }
};

export const getFriendlyAttributesOptions = (
  translate: (key: string) => string = (key) => key
) =>
  FRIENDLY_PROPERTIES
    // We have no way to distinguish between Element.IfcName and Shape.Name in the current structure,
    // so we filter away shape options in dynamic quantities modal.
    // The long term fix here involves a more fundamental expansion of the attribute structure
    .filter(([category]) => !['Shape', 'Sparkel Properties'].includes(category))
    .map(([category, property]) => ({
      label: displayNameFromFriendlyAttribute(property, translate),
      value: `${category},${property}`,
    }));
